import { resolveLocale, type SupportedLocale } from '@bln/shared/i18n';

/**
 * Widget UI copy (Story 4.1, Task 7). The 7-locale list + `resolveLocale` are
 * imported from `@bln/shared` — never re-derived here (coding-standards.md).
 *
 * Strings are seeded from ux/front-end-spec.md#Key Content Strings. English is
 * canonical; non-EN phrasing is DRAFT pending native-speaker / PO review — the
 * i18n WIRING (resolveLocale → content) is the deliverable, the exact phrasing
 * is reviewable data. Locales without a draft entry fall back to English via
 * `getContent`.
 */
export interface UiContent {
  /** Panel greeting (welcome state). */
  greeting: string;
  /** KB-support intro copy. */
  intro: string;
  /** Input placeholder. */
  inputPlaceholder: string;
  /** Persistent disclaimer beneath the input. */
  disclaimer: string;
  /** "Powered by" lockup prefix (the "skillspass" wordmark is styled separately). */
  poweredBy: string;
  /** SUGGESTED TOPICS section label. */
  suggestedTopicsLabel: string;
  /** Generic, safe inline error message (never leaks internals). */
  errorMessage: string;
  /** Retry affordance label. */
  retryLabel: string;
  /** Gentle prompt shown for empty / very short input (instead of sending). */
  shortInputHint: string;
  /** aria-label for the send button. */
  sendLabel: string;
  /** aria-label prefix for the assistant answer live region. */
  assistantLabel: string;
  /**
   * Localized "opens in new tab" phrase appended to citation-pill / video-card
   * accessible names (Story 4.2). The "Source" word itself comes from the shared
   * `citationLabel(locale)` — it is NOT duplicated here.
   */
  opensInNewTab: string;
  /** Accessible-name prefix for a VideoCard's play affordance (Interaction 2). */
  playVideoLabel: string;
  /** Accessible name for the 👍 per-answer feedback control (Story 4.3). */
  thumbUpLabel: string;
  /** Accessible name for the 👎 per-answer feedback control (Story 4.3). */
  thumbDownLabel: string;
  /** Small acknowledgement shown after a 👍/👎 is submitted. */
  feedbackThanks: string;
  /** End-of-conversation resolution question ("Did this resolve your issue?"). */
  resolutionPrompt: string;
  /** Resolution prompt "Yes" affordance. */
  resolutionYes: string;
  /** Resolution prompt "No" affordance. */
  resolutionNo: string;
  /** Accessible name for the dismiss (✕) control on the resolution prompt. */
  dismissLabel: string;
  /** Ticket-escalation CTA label ("Open a support ticket"). */
  openTicketLabel: string;
  /** Host-header launcher button label ("Ask"). */
  askLabel: string;
  /** Docked-panel title in the gradient header ("AI Helper"). */
  panelTitle: string;
  /** Accessible name for the panel's ✕ close control. */
  closeLabel: string;
  /** Accessible name for the header "new chat" control. */
  newChatLabel: string;
  /** Accessible name for the header "history" control. */
  historyLabel: string;
  /** Label on the "scroll to newest" pill shown when a message arrives while scrolled up. */
  newMessagesLabel: string;
  /**
   * REQ-034: note shown when the user wrote in another language than the
   * interface. Written in the language the user TYPED (they evidently read it);
   * the bot replies in that language too, but links interface-language articles.
   * `{detected}` / `{interface}` are language names in this same language.
   */
  languageNoticeLead: string;
  /** Appended when the tenant offers the user's language, so switching helps. */
  languageNoticeSwitch: string;
}

const EN: UiContent = {
  greeting: 'Hi! How can I help you today?',
  intro:
    'I can answer questions from the SkillsPass knowledge base — like how to start your assigned training, access your certificates, or troubleshoot an issue.',
  inputPlaceholder: 'Ask me anything…',
  disclaimer: 'AI responses are generated. Please verify key safety guidelines.',
  poweredBy: 'Powered by',
  suggestedTopicsLabel: 'Suggested topics',
  errorMessage:
    'Something went wrong reaching the assistant. Your conversation is safe — please try again.',
  retryLabel: 'Retry',
  shortInputHint: 'Please type a little more so I can help.',
  sendLabel: 'Send',
  assistantLabel: 'Assistant',
  opensInNewTab: 'opens in new tab',
  playVideoLabel: 'Play video',
  thumbUpLabel: 'This answer was helpful',
  thumbDownLabel: 'This answer was not helpful',
  feedbackThanks: 'Thanks for your feedback',
  resolutionPrompt: 'Did this resolve your issue?',
  resolutionYes: 'Yes',
  resolutionNo: 'No',
  dismissLabel: 'Dismiss',
  openTicketLabel: 'Open a support ticket',
  askLabel: 'Ask',
  panelTitle: 'AI Helper',
  closeLabel: 'Close',
  newChatLabel: 'New chat',
  historyLabel: 'Chat history',
  newMessagesLabel: 'New message',
  languageNoticeLead:
    'You wrote in {detected}, so I’ll reply in {detected} — but this help is set to {interface}, so the articles I link are in {interface}.',
  languageNoticeSwitch:
    'To get {detected} articles, change the language in the knowledge base or your app’s settings. Not sure how? Just ask me.',
};

// DRAFT — non-EN phrasing pending native-speaker / PO review (see module note).
const FR: UiContent = {
  greeting: "Bonjour ! Comment puis-je vous aider aujourd'hui ?",
  intro:
    'Je peux répondre aux questions à partir de la base de connaissances SkillsPass — par exemple comment démarrer votre formation assignée, accéder à vos certificats ou résoudre un problème.',
  inputPlaceholder: 'Posez-moi une question…',
  disclaimer:
    'Les réponses sont générées par IA. Veuillez vérifier les consignes de sécurité importantes.',
  poweredBy: 'Propulsé par',
  suggestedTopicsLabel: 'Sujets suggérés',
  errorMessage:
    "Un problème est survenu lors de la connexion à l'assistant. Votre conversation est conservée — veuillez réessayer.",
  retryLabel: 'Réessayer',
  shortInputHint: 'Veuillez écrire un peu plus pour que je puisse vous aider.',
  sendLabel: 'Envoyer',
  assistantLabel: 'Assistant',
  opensInNewTab: 'ouvre dans un nouvel onglet',
  playVideoLabel: 'Lire la vidéo',
  thumbUpLabel: 'Cette réponse a été utile',
  thumbDownLabel: "Cette réponse n'a pas été utile",
  feedbackThanks: 'Merci pour votre commentaire',
  resolutionPrompt: 'Est-ce que cela a résolu votre problème ?',
  resolutionYes: 'Oui',
  resolutionNo: 'Non',
  dismissLabel: 'Fermer',
  openTicketLabel: 'Ouvrir un billet de support',
  askLabel: 'Demander',
  panelTitle: 'Assistant IA',
  closeLabel: 'Fermer',
  newChatLabel: 'Nouvelle conversation',
  historyLabel: 'Historique',
  newMessagesLabel: 'Nouveau message',
  languageNoticeLead:
    'Vous avez écrit en {detected}, je vous réponds donc en {detected} — mais cette aide est configurée en {interface} : les articles proposés sont donc en {interface}.',
  languageNoticeSwitch:
    'Pour obtenir des articles en {detected}, changez la langue dans la base de connaissances ou dans les paramètres de votre application. Vous ne savez pas comment ? Demandez-le-moi.',
};

/** DRAFT locale table; unlisted locales fall back to EN. */
const CONTENT: Partial<Record<SupportedLocale, UiContent>> = {
  en: EN,
  fr: FR,
};

/**
 * Resolve UI copy for an arbitrary request/handshake locale. Routes through the
 * shared `resolveLocale` (the ONE place a locale becomes one of the 7 or the EN
 * fallback), then returns the draft table entry or EN.
 */
export function getContent(locale: string): UiContent {
  return CONTENT[resolveLocale(locale)] ?? EN;
}

/**
 * Cold-start static suggested topics (KB-answerable only), used as the default
 * fallback when a tenant provides none. Phrasing per
 * ux/front-end-spec.md#Key Content Strings.
 */
export const DEFAULT_SUGGESTED_TOPICS = [
  { label: 'How do I start my assigned course?', seedQuery: 'How do I start my assigned course?' },
  { label: 'How do I access my certificate?', seedQuery: 'How do I access my certificate?' },
  {
    label: "Training isn't working as expected",
    seedQuery: "My training isn't working as expected — how do I troubleshoot it?",
  },
] as const;
